import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import KpiCard from '../../components/crm/KpiCard';
import PipelineChart from '../../components/crm/PipelineChart';
import Sparkline from '../../components/crm/Sparkline';
import { getCrmKpis, getPipeline } from '../../services/mock/crmService';
import { getCompanies } from '../../services/mock/companyService';

const AdvancedAnalytics = () => {
  const { org } = useOrg();
  const [kpis, setKpis] = useState({});
  const [pipeline, setPipeline] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('90d');
  const [metricType, setMetricType] = useState('revenue');

  const showGMV = process.env.REACT_APP_SHOW_GMV === 'true';

  useEffect(() => {
    loadData();
  }, [org, dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const filters = { dateRange };
      const [kpisData, pipelineData, companiesData] = await Promise.all([
        getCrmKpis(org, filters),
        getPipeline(org, filters),
        getCompanies(org)
      ]);

      setKpis(kpisData);
      setPipeline(pipelineData);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => `$${value?.toLocaleString() || 0}`;
  const formatPercent = (value) => `${((value || 0) * 100).toFixed(1)}%`;

  // Generate trend data
  const revenueData = Array.from({ length: 12 }, () => Math.floor(50000 + Math.random() * 100000));
  const conversionData = Array.from({ length: 12 }, () => Math.random() * 0.4 + 0.3);
  const customerData = Array.from({ length: 12 }, () => Math.floor(5 + Math.random() * 15));

  // Advanced metrics calculations
  const totalRevenue = companies.reduce((sum, c) => sum + (c.revenue12m || 0), 0);
  const customerLifetimeValue = totalRevenue / Math.max(companies.length, 1) * 2.5;
  const customerAcquisitionCost = customerLifetimeValue * 0.25;
  const churnRate = 0.08; // 8% monthly churn
  const marketPenetration = 0.34; // 34% market penetration

  if (loading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  return (
    <Box>
      <Breadcrumbs />
      
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
            Advanced Analytics
          </Typography>
          <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500 }}>
            {org === 'bare' ? 'Bare Biologics - Predictive Insights' : 'EPL - Performance Intelligence'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Time Period"
            >
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
              <MenuItem value="180d">Last 6 months</MenuItem>
              <MenuItem value="365d">Last year</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Metric Focus</InputLabel>
            <Select
              value={metricType}
              onChange={(e) => setMetricType(e.target.value)}
              label="Metric Focus"
            >
              <MenuItem value="revenue">Revenue Focus</MenuItem>
              <MenuItem value="customers">Customer Focus</MenuItem>
              <MenuItem value="performance">Performance Focus</MenuItem>
            </Select>
          </FormControl>
          
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Advanced KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {showGMV && (
          <Grid item xs={12} sm={6} md={2}>
            <KpiCard
              title="Customer LTV"
              value={customerLifetimeValue}
              subtitle="lifetime value"
              icon={<TrendingUpIcon />}
              color="#10b981"
              formatter={formatCurrency}
              trend="up"
              trendValue={15.3}
            />
          </Grid>
        )}
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2.4}>
          <KpiCard
            title="CAC"
            value={customerAcquisitionCost}
            subtitle="acquisition cost"
            icon={<BusinessIcon />}
            color="#f59e0b"
            formatter={formatCurrency}
            trend="down"
            trendValue={-8.2}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2.4}>
          <KpiCard
            title="Churn Rate"
            value={churnRate}
            subtitle="monthly churn"
            icon={<SpeedIcon />}
            color="#ef4444"
            formatter={formatPercent}
            trend="down"
            trendValue={-12.1}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2.4}>
          <KpiCard
            title="Market Share"
            value={marketPenetration}
            subtitle="penetration rate"
            icon={<AssessmentIcon />}
            color="#8b5cf6"
            formatter={formatPercent}
            trend="up"
            trendValue={4.7}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2.4}>
          <KpiCard
            title="Conversion Rate"
            value={kpis.winRate90d || 0.42}
            subtitle="lead to customer"
            icon={<TimelineIcon />}
            color="#039582"
            formatter={formatPercent}
            trend="up"
            trendValue={6.8}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={showGMV ? 2 : 2.4}>
          <KpiCard
            title="Sales Velocity"
            value={kpis.velocityDays}
            subtitle="avg days to close"
            icon={<SpeedIcon />}
            color="#f97316"
            formatter={(val) => `${val} days`}
            trend="down"
            trendValue={-11.5}
          />
        </Grid>
      </Grid>

      {/* Analytics Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Trends */}
        <Grid item xs={12} md={8}>
          <Card sx={{ border: '1px solid #e2e8f0', height: '400px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Revenue Trend Analysis (12 months)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                <Sparkline 
                  data={revenueData} 
                  width={300} 
                  height={80}
                  color={org === 'bare' ? '#5c725c' : '#0d9488'}
                />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    {formatCurrency(revenueData[revenueData.length - 1])}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Current month
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    YoY Growth
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#10b981' }}>
                    +23.4%
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    Avg Monthly
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(revenueData.reduce((a, b) => a + b) / revenueData.length)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    Forecast Next
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#f59e0b' }}>
                    {formatCurrency(revenueData[revenueData.length - 1] * 1.12)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Conversion Funnel */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', height: '400px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Conversion Funnel
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Leads</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>1,234</Typography>
                </Box>
                <Box sx={{ height: 8, bgcolor: '#f1f5f9', borderRadius: 1, mb: 2 }}>
                  <Box sx={{ height: '100%', width: '100%', bgcolor: '#64748b', borderRadius: 1 }} />
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Qualified</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>892</Typography>
                </Box>
                <Box sx={{ height: 8, bgcolor: '#f1f5f9', borderRadius: 1, mb: 2 }}>
                  <Box sx={{ height: '100%', width: '72%', bgcolor: '#0d9488', borderRadius: 1 }} />
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Proposals</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>456</Typography>
                </Box>
                <Box sx={{ height: 8, bgcolor: '#f1f5f9', borderRadius: 1, mb: 2 }}>
                  <Box sx={{ height: '100%', width: '37%', bgcolor: '#f59e0b', borderRadius: 1 }} />
                </Box>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Customers</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>234</Typography>
                </Box>
                <Box sx={{ height: 8, bgcolor: '#f1f5f9', borderRadius: 1, mb: 2 }}>
                  <Box sx={{ height: '100%', width: '19%', bgcolor: '#10b981', borderRadius: 1 }} />
                </Box>
              </Box>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                  Overall Conversion Rate
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
                  18.9%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pipeline Analysis */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent>
              <PipelineChart deals={pipeline} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Performance Predictions
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                  Revenue Forecast (Next Quarter)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981', mb: 2 }}>
                  {formatCurrency(totalRevenue * 1.15)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#10b981' }}>
                  ↗ 15% increase predicted
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                  Customer Growth Rate
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b', mb: 2 }}>
                  +12.3%
                </Typography>
                <Typography variant="body2" sx={{ color: '#f59e0b' }}>
                  ↗ Above industry average
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                  Risk Assessment
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ef4444', mb: 2 }}>
                  Medium
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  2 accounts at risk, contract renewals needed
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdvancedAnalytics;
